// src/pages/Log.js
import React, { useState, useCallback, useEffect } from 'react';
import api from '../utils/api';
import { formatDate, formatDateTime, roleLabel } from '../utils/helpers';
import { PageWrapper } from './Dashboard';

export default function Log() {
  const [clientName, setClientName] = useState('');
  const [date, setDate] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const [hideLate, setHideLate] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    // Load both active and discharged clients for autocomplete
    Promise.all([
      api.get('/clients'),
      api.get('/clients/discharged')
    ]).then(([activeRes, dischargedRes]) => {
      const active = activeRes.data || [];
      const discharged = dischargedRes.data || [];
      setAllClients([...active, ...discharged]);
    }).catch(() => {});
  }, []);

  const handleNameChange = (val) => {
    setSelectedClientId(null);
    setClientName(val);
    if (val.trim().length >= 1) {
      const q = val.toLowerCase();
      const matches = allClients.filter(c =>
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(q)
      ).slice(0, 8);
      setSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (client) => {
    setSelectedClientId(client.id);
    setClientName(`${client.first_name} ${client.last_name}`);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const search = useCallback(async () => {
    setLoading(true);
    setSearched(true);
    try {
      const params = {};
      if (date) params.date = date;
      if (fromTime) params.fromTime = fromTime;
      if (toTime) params.toTime = toTime;

      if (selectedClientId) params.clientId = selectedClientId;
      const res = await api.get("/checks", { params: { ...params, limit: 500 } });
      let data = res.data;

      if (clientName.trim()) {
        const q = clientName.toLowerCase();
        data = data.filter(c => c.client_name?.toLowerCase().includes(q));
      }

      setResults(data);
    } catch (err) {
      console.error('Search error', err);
    } finally {
      setLoading(false);
    }
  }, [clientName, date, fromTime, toTime, selectedClientId]);

  const handleKeyDown = (e) => { if (e.key === 'Enter') search(); };

  const handleSelectCheck = (check) => {
    setSelectedCheck(selectedCheck?.id === check.id ? null : check);
  };

  const exportPDF = async (check) => {
    try {
      const res = await api.get('/reports/client-checks', {
        params: { clientId: check.client_id, fromDate: date || undefined, toDate: date || undefined, hideLate },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `safety-log-${check.client_name?.replace(/\s+/g, '-')}.pdf`;
      a.click();
    } catch { alert('Failed to generate PDF'); }
  };

  const exportCSV = async (check) => {
    try {
      const res = await api.get('/reports/client-checks-csv', {
        params: { clientId: check.client_id, fromDate: date || undefined, toDate: date || undefined, hideLate },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `safety-log-${check.client_name?.replace(/\s+/g, '-')}.csv`;
      a.click();
    } catch { alert('Failed to generate CSV'); }
  };

  const exportAllPDF = async () => {
    if (!results.length) return;
    const clientId = results[0]?.client_id;
    if (!clientId) return;
    try {
      const res = await api.get('/reports/client-checks', {
        params: { clientId, fromDate: date || undefined, toDate: date || undefined, hideLate },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `safety-log-${results[0]?.client_name?.replace(/\s+/g, '-')}.pdf`;
      a.click();
    } catch { alert('Failed to generate PDF'); }
  };

  const displayResults = hideLate ? results.filter(r => !r.is_late) : results;

  return (
    <PageWrapper active="log" title="Check Log">

      {/* Search filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="field" style={{ gridColumn: '1 / -1', marginBottom: 0, position: 'relative' }}>
            <label>Client name</label>
            <input
              placeholder="Search by name..."
              value={clientName}
              onChange={e => handleNameChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              onFocus={() => clientName && suggestions.length > 0 && setShowSuggestions(true)}
              autoComplete="off"
            />
            {showSuggestions && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #E5E7EB', borderRadius: '0 0 8px 8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: 240, overflowY: 'auto' }}>
                {suggestions.map(c => (
                  <div
                    key={c.id}
                    onMouseDown={() => selectSuggestion(c)}
                    style={{ padding: '9px 14px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <span>{c.first_name} {c.last_name}</span>
                    {c.discharge_date && (
                      <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 8 }}>Discharged</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>From time</label>
            <input type="time" value={fromTime} onChange={e => setFromTime(e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>To time</label>
            <input type="time" value={toTime} onChange={e => setToTime(e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4B5563', cursor: 'pointer' }}>
            <input type="checkbox" checked={hideLate} onChange={e => setHideLate(e.target.checked)} style={{ width: 'auto' }} />
            Hide "late" labels in export
          </label>
          <button className="btn btn-primary btn-sm" onClick={search}>Search</button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setClientName(''); setDate(''); setFromTime(''); setToTime(''); setResults([]); setSearched(false); }}>Clear</button>
        </div>
      </div>

      {/* Export bar */}
      {searched && results.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#6B7280' }}>{displayResults.length} result{displayResults.length !== 1 ? 's' : ''}</span>
          <button className="btn btn-secondary btn-sm" onClick={exportAllPDF}>Export PDF</button>
          <button className="btn btn-secondary btn-sm" onClick={() => exportCSV(results[0])}>Export CSV</button>
        </div>
      )}

      {loading && <div className="spinner" />}

      {!loading && searched && displayResults.length === 0 && (
        <div className="empty-state">No checks found for this search.</div>
      )}

      {!loading && displayResults.map(check => (
        <div
          key={check.id}
          className={`log-entry ${check.is_late && !hideLate ? 'late' : ''}`}
          onClick={() => handleSelectCheck(check)}
          style={{ cursor: 'pointer' }}
        >
          <div className="le-time">{formatDateTime(check.check_datetime)}</div>
          <div className="le-client">
            {check.client_name}
            <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400, marginLeft: 8 }}>
              DOB: {formatDate(check.client_dob)}
            </span>
            {check.is_late && !hideLate && (
              <span className="badge badge-late" style={{ marginLeft: 8, fontSize: 10 }}>
                Late {check.minutes_late}m
              </span>
            )}
          </div>
          <div className="le-detail">{check.location} — {check.activity}</div>
          <div className="le-detail">Mood: {check.mood}{check.notes ? ` · ${check.notes}` : ''}</div>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
            Staff: {check.staff_name} ({roleLabel(check.staff_role)})
          </div>

          {selectedCheck?.id === check.id && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #E5E7EB' }}>
              {/* Electronic signature attestation */}
              {check.signed_by ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, padding: '8px 10px', background: '#F0FDF4', borderRadius: 6, border: '1px solid #BBF7D0' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  <div>
                    <div style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>Electronically signed</div>
                    <div style={{ fontSize: 11, color: '#166534' }}>
                      {check.signed_by} · {formatDateTime(check.signed_at)}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 10, fontStyle: 'italic' }}>
                  No electronic signature on file
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); exportPDF(check); }}>Export PDF</button>
                <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); exportCSV(check); }}>Export CSV</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </PageWrapper>
  );
}
